import { useTranslations } from '@vocab/react';
import { FieldMessage } from 'braid-design-system';
import { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import lysTranslations from 'src/pages/LinkYourSoftware/.vocab';
import type { FormValues } from 'src/pages/LinkYourSoftware/components/ContentState';
import type { Partner } from 'src/types';
import { useIntegrationPartners } from 'src/utils/hooks/useIntegrationPartners';
import { chooseReleaseNotesPartners } from 'src/utils/partners';

import { RadioWithFollowUp } from '../RadioWithFollowUp/RadioWithFollowUp';

import translations from './.vocab';

export const FutureApplications = ({ partner }: { partner: Partner }) => {
  const { t: lys } = useTranslations(lysTranslations);
  const { t } = useTranslations(translations);

  const { trigger } = useFormContext<FormValues>();

  const releaseNotesPartners = chooseReleaseNotesPartners(
    useIntegrationPartners().partners,
  );

  const partnerList =
    releaseNotesPartners.state === 'data'
      ? releaseNotesPartners.data.map(({ name }) => name)
      : [];

  const hasOptimisedApply = partner.configurableRelationships?.some(
    ({ typeCode }) => typeCode === 'APPLICATION_EXPORT',
  );

  const radioOptions = [
    {
      label: lys('advertiserCenter'),
      content: hasOptimisedApply ? (
        <FieldMessage
          tone="caution"
          id="lys-optimised-apply-warning"
          message={t(
            'You won’t be able to receive applications in {partnerName}',
            {
              partnerName: partner.name,
            },
          )}
        />
      ) : null,
    },
    { label: lys('throughAnotherRsp') },
  ] as const;

  const triggerFutureApplications = useCallback(
    () => trigger('futureApplications'),
    [trigger],
  );

  return (
    <Controller
      name="futureApplications"
      rules={{
        required: lys('selectOneOption'),
        validate: (value: FormValues['futureApplications']) => {
          if (
            value?.answer === radioOptions[1].label &&
            !value?.followUpAnswer
          ) {
            return lys('selectOneOption');
          }

          if (
            value?.answer === radioOptions[1].label &&
            value?.followUpAnswer === partner.name &&
            hasOptimisedApply
          ) {
            return t(
              'Choose SEEK Optimised Apply to receive applications in {partnerName}',
              { partnerName: partner.name },
            );
          }

          return true;
        },
      }}
      render={({ field, fieldState }) => (
        <RadioWithFollowUp
          id="lys-future-applications"
          radioLabel={lys('receivingApplications')}
          radioOptions={radioOptions}
          dropdownLabel={lys('whichRspFromSeek')}
          dropdownOptions={partnerList}
          value={field.value}
          trigger={triggerFutureApplications}
          onChange={field.onChange}
          error={fieldState.invalid ? fieldState.error?.message : undefined}
        />
      )}
    />
  );
};
